import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import RecipeList from '../components/recipes/RecipeList';
import UpgradeBanner from '../components/subscription/UpgradeBanner';
import { Plus, Link as LinkIcon, Filter, ChevronDown, Globe2, Baby, Dumbbell, Flame, X } from 'lucide-react';
import { AGE_GROUPS, DIFFICULTY_LEVELS, DIETARY_TAGS, COOKING_STYLE_TAGS, CONVENIENCE_TAGS } from '../constants/recipeConstants';

const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    aria-pressed={!!active}
    className={[
      'px-3 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap transition-colors',
      active
        ? 'bg-primary-600 border-primary-600 text-white'
        : 'bg-white dark:bg-night-surface border-gray-200 dark:border-night-card text-gray-700 dark:text-night-text hover:border-gray-300',
    ].join(' ')}
    {...rest}
  >
    {children}
  </button>
);

const FilterSection = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-night-muted mb-2">
      <Icon size={14} aria-hidden="true" />
      <span>{title}</span>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export default function RecipesPage() {
  const {
    filteredRecipes, categories, activeCategory, setActiveCategory, searchQuery,
    activeCuisine, setActiveCuisine,
    activeAgeGroup, setActiveAgeGroup,
    activeDietary, setActiveDietary,
    activeDifficulty, setActiveDifficulty,
    availableCuisines,
  } = useApp();
  const { canAddRecipe, promptUpgrade } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);

  const activeFilterCount = [
    activeCuisine !== 'All' ? 1 : 0,
    activeAgeGroup !== 'All' ? 1 : 0,
    activeDifficulty !== 'All' ? 1 : 0,
    activeDietary.length,
  ].reduce((a, b) => a + b, 0);

  const toggleDietaryTag = (tag) => {
    setActiveDietary(activeDietary.includes(tag) ? activeDietary.filter((t) => t !== tag) : [...activeDietary, tag]);
  };

  const clearAllFilters = () => {
    setActiveCuisine('All');
    setActiveAgeGroup('All');
    setActiveDifficulty('All');
    setActiveDietary([]);
  };

  const allDietaryOptions = [...DIETARY_TAGS, ...COOKING_STYLE_TAGS, ...CONVENIENCE_TAGS];

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const filterParam = searchParams.get('filter');
    if (filterParam === 'favorites') setActiveCategory('Favorites');
    else if (categoryParam) setActiveCategory(categoryParam);
    return () => setActiveCategory('All');
  }, [searchParams, setActiveCategory]);

  const allCategories = ['All', 'Favorites', ...categories.map((c) => c.name)];

  const guardAndNavigate = (path) => {
    const { allowed } = canAddRecipe();
    if (!allowed) { promptUpgrade('recipe_limit'); return; }
    navigate(path);
  };

  return (
    <div className="space-y-4">
      <UpgradeBanner />

      <div className="space-y-3">
        <div className="-mx-4 px-4 overflow-x-auto">
          <div className="flex gap-2 pb-1">
            {allCategories.map((cat) => (
              <Chip key={cat} active={activeCategory === cat} onClick={() => setActiveCategory(cat)}>
                {cat}
              </Chip>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            className={[
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
              showFilters
                ? 'bg-primary-50 border-primary-200 text-primary-700 dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-400'
                : 'bg-white dark:bg-night-surface border-gray-200 dark:border-night-card text-gray-700 dark:text-night-text',
            ].join(' ')}
          >
            <Filter size={16} aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary-600 text-white text-[11px] font-semibold">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown size={14} className={['transition-transform', showFilters ? 'rotate-180' : ''].join(' ')} aria-hidden="true" />
          </button>
        </div>

        {showFilters && (
          <div className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-4 space-y-4">
            <FilterSection icon={Globe2} title="Cuisine">
              <Chip active={activeCuisine === 'All'} onClick={() => setActiveCuisine('All')}>All</Chip>
              {availableCuisines.map((c) => (
                <Chip key={c} active={activeCuisine === c} onClick={() => setActiveCuisine(c)}>{c}</Chip>
              ))}
            </FilterSection>

            <FilterSection icon={Baby} title="Age Group">
              <Chip active={activeAgeGroup === 'All'} onClick={() => setActiveAgeGroup('All')}>All</Chip>
              {Object.entries(AGE_GROUPS).map(([key, { label }]) => (
                <Chip key={key} active={activeAgeGroup === key} onClick={() => setActiveAgeGroup(key)}>{label}</Chip>
              ))}
            </FilterSection>

            <FilterSection icon={Flame} title="Difficulty">
              <Chip active={activeDifficulty === 'All'} onClick={() => setActiveDifficulty('All')}>All</Chip>
              {Object.entries(DIFFICULTY_LEVELS).map(([key, { label }]) => (
                <Chip key={key} active={activeDifficulty === key} onClick={() => setActiveDifficulty(key)}>{label}</Chip>
              ))}
            </FilterSection>

            <FilterSection icon={Dumbbell} title="Dietary & Style">
              {allDietaryOptions.map((tag) => (
                <Chip key={tag} active={activeDietary.includes(tag)} onClick={() => toggleDietaryTag(tag)}>{tag}</Chip>
              ))}
            </FilterSection>

            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-night-muted hover:underline"
              >
                <X size={14} aria-hidden="true" />
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      <div className="text-sm text-gray-500 dark:text-night-muted">
        {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? 's' : ''}
        {searchQuery && <span> for "{searchQuery}"</span>}
      </div>

      <RecipeList recipes={filteredRecipes} />

      <div className="fixed bottom-20 right-4 z-30 flex flex-col items-center gap-2 md:bottom-4">
        <button
          onClick={() => guardAndNavigate('/import')}
          aria-label="Import from URL"
          className="w-12 h-12 rounded-full bg-white dark:bg-night-surface border border-gray-200 dark:border-night-card text-primary-600 dark:text-primary-400 shadow-md flex items-center justify-center hover:scale-105 transition-transform"
        >
          <LinkIcon size={20} />
        </button>
        <button
          onClick={() => guardAndNavigate('/submit')}
          aria-label="Add recipe"
          className="w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        >
          <Plus size={26} />
        </button>
      </div>
    </div>
  );
}
